// completionVariance.js - Modal content for Completion Variance KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/completionVariance.js

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import useModuleVarianceData from "../../../hooks/useModuleVarianceData";
import useTableSort from "../../../hooks/useTableSort";
import SortableTableHeader from "../../modalContent/SortableTableHeader";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const v = payload[0].value;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: v >= 0 ? "#38a169" : "#cc0000" }}
        />
        {v >= 0 ? `+${v}` : v}% variance
      </div>
    </div>
  );
};

const CompletionVariance = () => {
  const { metrics, graph, table, loading, error } = useModuleVarianceData();

  // ── Table sorting — must be called before any early returns ───────────────
  const { sortedData, requestSort, getSortClass } = useTableSort(table, {
    initialKey: "woNum",
    initialDirection: "asc",
  });

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="pml-modal-flex-content pml-modal-loading">
        Loading completion variance data…
      </div>
    );
  }

  if (error) {
    return (
      <div className="pml-modal-flex-content pml-modal-error">{error}</div>
    );
  }

  const { eightWeekVariancePct, weeksAhead, weeksBehind } = metrics;

  return (
    <div className="pml-modal-flex-content">
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--3">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">8-Week Avg Variance</span>
          <span
            className={`pml-modal-stat-value ${eightWeekVariancePct <= 0 ? "pml-modal-stat-value--ok" : "pml-modal-stat-value--alert"}`}
          >
            {eightWeekVariancePct >= 0
              ? `+${eightWeekVariancePct}`
              : eightWeekVariancePct}
            %
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Ahead</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {weeksAhead}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Behind</span>
          <span
            className={`pml-modal-stat-value ${weeksBehind > 0 ? "pml-modal-stat-value--alert" : "pml-modal-stat-value--ok"}`}
          >
            {weeksBehind}
          </span>
        </div>
      </div>

      {/* Bar chart — 8-week variance trend */}
      <p className="pml-modal-sub-title">8-Week Variance Trend</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={graph}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}%`}
              width={42}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#cbd5e0" strokeWidth={1.5} />
            <Bar dataKey="variancePct" radius={[4, 4, 0, 0]}>
              {graph.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.variancePct <= 0 ? "#38a169" : "#cc0000"}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-module variance table */}
      <p className="pml-modal-sub-title">Module-Level Variance</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <SortableTableHeader
                columnKey="woNum"
                onSort={requestSort}
                sortClass={getSortClass("woNum")}
              >
                Work Order
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="scheduled"
                onSort={requestSort}
                sortClass={getSortClass("scheduled")}
              >
                Scheduled Completion
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="actual"
                onSort={requestSort}
                sortClass={getSortClass("actual")}
              >
                Actual Completion
              </SortableTableHeader>
              <SortableTableHeader
                columnKey="varianceDays"
                onSort={requestSort}
                sortClass={getSortClass("varianceDays")}
              >
                Variance (days)
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, i) => {
              const cls =
                row.varianceDays === 0
                  ? "pml-variance-cell--zero"
                  : row.varianceDays < 0
                    ? "pml-variance-cell--pos"
                    : "pml-variance-cell--neg";
              return (
                <tr key={i}>
                  <td>{row.woNum}</td>
                  <td>{row.scheduled}</td>
                  <td>{row.actual}</td>
                  <td className={cls}>
                    {row.varianceDays === 0
                      ? "On time"
                      : row.varianceDays < 0
                        ? `${Math.abs(row.varianceDays)}d early`
                        : `${row.varianceDays}d late`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompletionVariance;
